import { useState, useEffect } from 'react'
import type { QcProgram } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import {
  PageHeader,
  DataTable,
  Pagination,
  Modal,
  FormField,
  ConfirmDialog,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  btnDanger,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'
import { useTableColumns } from '../../hooks/use-table-columns'

interface Form {
  code: string
  name: string
  description: string
  enrollmentId: string
  status: string
  startDate: string
  endDate: string
  scheduledEventsJson: string
  documentsJson: string
}

const defaults: Form = {
  code: '',
  name: '',
  description: '',
  enrollmentId: '',
  status: 'active',
  startDate: '',
  endDate: '',
  scheduledEventsJson: '[]',
  documentsJson: '[]',
}

export function QcProgramsPage() {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage, refresh } = useApiList<QcProgram>({
    path: '/qc-programs',
    filters: { search: debouncedSearch || undefined },
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('qc-programs')
  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState<QcProgram | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [form, setForm] = useState<Form>(defaults)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)

  useEffect(() => {
    if (!modalOpen) return
    setError(null)
    if (isNew || !selected) {
      setForm(defaults)
      return
    }
    setForm({
      code: selected.code,
      name: selected.name,
      description: selected.description ?? '',
      enrollmentId: selected.enrollmentId ?? '',
      status: selected.status,
      startDate: selected.startDate ? selected.startDate.slice(0, 10) : '',
      endDate: selected.endDate ? selected.endDate.slice(0, 10) : '',
      scheduledEventsJson: JSON.stringify(selected.scheduledEvents, null, 2),
      documentsJson: JSON.stringify(selected.documents, null, 2),
    })
  }, [modalOpen, isNew, selected])

  const update = <K extends keyof Form>(k: K, v: Form[K]) => setForm((prev) => ({ ...prev, [k]: v }))

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      let scheduledEvents: unknown
      let documents: unknown
      try {
        scheduledEvents = JSON.parse(form.scheduledEventsJson || '[]')
        documents = JSON.parse(form.documentsJson || '[]')
        if (!Array.isArray(scheduledEvents) || !Array.isArray(documents)) throw new Error('must be arrays')
      } catch (err) {
        setError(`JSON invalid: ${err instanceof Error ? err.message : String(err)}`)
        setSaving(false)
        return
      }
      const payload: Record<string, unknown> = {
        code: form.code,
        name: form.name,
        status: form.status,
        scheduledEvents,
        documents,
      }
      if (form.description) payload.description = form.description
      if (form.enrollmentId) payload.enrollmentId = form.enrollmentId
      if (form.startDate) payload.startDate = `${form.startDate}T00:00:00.000Z`
      if (form.endDate) payload.endDate = `${form.endDate}T23:59:59.000Z`
      if (isNew) await api.post<QcProgram>('/qc-programs', payload)
      else await api.put<QcProgram>(`/qc-programs/${selected?.id}`, payload)
      refresh()
      setModalOpen(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive() {
    if (!selected) return
    setArchiving(true)
    try {
      await api.delete(`/qc-programs/${selected.id}`)
      refresh()
      archiveModal.close()
      setModalOpen(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive')
    } finally {
      setArchiving(false)
    }
  }

  const columns: readonly Column<QcProgram>[] = [
    { key: 'code', header: 'Code', sortable: true, filterable: true, render: (p) => <strong>{p.code}</strong> },
    { key: 'name', header: 'Name', sortable: true, filterable: true, render: (p) => p.name },
    { key: 'enrollment', header: 'Enrollment ID', render: (p) => p.enrollmentId || '--' },
    { key: 'events', header: 'Events', render: (p) => `${p.scheduledEvents.length}` },
    { key: 'docs', header: 'Docs', render: (p) => `${p.documents.length}` },
    { key: 'status', header: 'Status', sortable: true, render: (p) => p.status },
  ]

  const title = isNew ? 'New QC Program' : `${form.code} - ${form.name}`

  return (
    <div>
      <PageHeader
        title="QC Programs"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button
              type="button"
              onClick={() => {
                setSelected(null)
                setIsNew(true)
                setModalOpen(true)
              }}
              style={btnPrimary}
            >
              Add Program
            </button>
          </>
        }
      />
      <p style={{ color: colors.textMuted, fontSize: '0.875rem', margin: '0 0 1rem' }}>
        Proficiency-testing enrollments (CAP, API, etc.). Each program tracks scheduled events, uploaded documents, and
        status.
      </p>
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '320px' }}
          placeholder="Search by code or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(p) => {
              setSelected(p)
              setIsNew(false)
              setModalOpen(true)
            }}
            emptyMessage="No QC programs yet."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={title} width="780px">
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '0.75rem' }}>
            <FormField name="code" label="Code" required value={form.code} onChange={(v) => update('code', v)} />
            <FormField name="name" label="Name" required value={form.name} onChange={(v) => update('name', v)} />
            <FormField name="status" label="Status" required>
              <select style={input} value={form.status} onChange={(e) => update('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="expired">Expired</option>
              </select>
            </FormField>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
            <FormField
              name="enrollmentId"
              label="Enrollment ID"
              value={form.enrollmentId}
              onChange={(v) => update('enrollmentId', v)}
            />
            <FormField
              name="startDate"
              label="Start Date"
              type="date"
              value={form.startDate}
              onChange={(v) => update('startDate', v)}
            />
            <FormField
              name="endDate"
              label="End Date"
              type="date"
              value={form.endDate}
              onChange={(v) => update('endDate', v)}
            />
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FormField name="description" label="Description">
              <textarea
                style={{ ...input, minHeight: '60px', resize: 'vertical' }}
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
              />
            </FormField>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FormField name="scheduledEventsJson" label="Scheduled Events (JSON array)">
              <textarea
                style={{
                  ...input,
                  minHeight: '100px',
                  resize: 'vertical',
                  fontFamily: 'monospace',
                  fontSize: '0.8125rem',
                }}
                value={form.scheduledEventsJson}
                onChange={(e) => update('scheduledEventsJson', e.target.value)}
                placeholder='[{"eventCode":"2026-Q1","dueDate":"2026-03-31T00:00:00Z","status":"scheduled"}]'
              />
            </FormField>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <FormField name="documentsJson" label="Documents (JSON array)">
              <textarea
                style={{
                  ...input,
                  minHeight: '100px',
                  resize: 'vertical',
                  fontFamily: 'monospace',
                  fontSize: '0.8125rem',
                }}
                value={form.documentsJson}
                onChange={(e) => update('documentsJson', e.target.value)}
                placeholder='[{"kind":"certificate","label":"Q1 2026 cert","storageRef":"s3://..."}]'
              />
            </FormField>
          </div>
          {error && (
            <div
              style={{
                marginTop: '0.75rem',
                color: colors.danger,
                padding: '0.5rem 0.75rem',
                background: colors.bgError,
                border: `1px solid ${colors.borderError}`,
                borderRadius: '3px',
                fontSize: '0.875rem',
              }}
            >
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
            {!isNew && (
              <button
                type="button"
                onClick={archiveModal.open}
                style={{ ...btnDanger, marginRight: 'auto' }}
                disabled={saving || archiving}
              >
                Archive
              </button>
            )}
            <button type="button" onClick={() => setModalOpen(false)} style={btnSecondary} disabled={saving}>
              Cancel
            </button>
            <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive QC Program"
        message={`Archive ${form.name}? Historical events and documents are preserved.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  )
}
